import { useEffect, useReducer } from "react";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { Save, ArrowBigRight, ArrowBigLeft } from "lucide-react";
import { useAdminCreateCompetition } from "@/services/competitionService";
import { useAdminGetSkillsSWR } from "@/services/skillService";



const initialState = {
    apiPayload: {
        name: '',
        code: '',
        active: true,
        default: false,
        skill_ids: [],
    },
    error_message: "",
    isLoading: false
}



const ACTIONS = {
    SET_API_PAYLOAD_FIELD: 'setApiPayloadField',
    ADD_SKILL_ID: 'addSkillId',
    REMOVE_SKILL_ID: 'removeSkillId',
    SET_ERROR_MESSAGE: 'setErrorMessage',
    SET_IS_LOADING: 'setIsLoading',
}



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_API_PAYLOAD_FIELD:
            return { ...state, apiPayload: { ...state.apiPayload, [action.subtype]: action.payload }, error_message: null }
        case ACTIONS.ADD_SKILL_ID:
            return { ...state, apiPayload: { ...state.apiPayload, skill_ids: [...state.apiPayload.skill_ids, Number(action.payload)] } }
        case ACTIONS.REMOVE_SKILL_ID:
            return { ...state, apiPayload: { ...state.apiPayload, skill_ids: state.apiPayload.skill_ids.filter((skill_id) => skill_id != action.payload) } }
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, error_message: action.payload }
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}

export default function Add() {

    const [ , setPageTitle,  , setShowBackButton] = useOutletContext()
    const [state, dispatch] = useReducer(reducer, initialState)

    const navigate = useNavigate()
    const location = useLocation()

    const {data: skills} = useAdminGetSkillsSWR(true)
    const adminCreateCompetition = useAdminCreateCompetition()



    /* On met à jour le titre */
    useEffect(() => {
        setPageTitle("Ajout d'une compétition")
        setShowBackButton(true)

        return () => {
            setPageTitle("Titre")
            setShowBackButton(false)
        }
    }, [setPageTitle, setShowBackButton])



    /* Gestion des changements de champs du formulaire */
    const handleChange = (e) => {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

        dispatch({ type: ACTIONS.SET_API_PAYLOAD_FIELD, 
            subtype: e.target.name, 
            payload: e.target.type == 'checkbox' ? e.target.checked : e.target.value
        })

        
    }



    /* Soumission du formulaire */
    const handleSubmit = async (e) => {
        e.preventDefault()

        if(!state.apiPayload.name || !state.apiPayload.code ) {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Veuillez remplir tous les champs obligatoires" });
            return 
        }

        const payload = {
            ...state.apiPayload
        }

        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

        try {
            await adminCreateCompetition(payload)
            navigate(location.state?.from ?? "/admin/skills")
        } catch (error) {
            console.error('admin/competitions/add::handleSubmit::error', error)
            if (error?.response?.status === 412) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Erreur de validation en base de données (code déjà utilisé, contrainte non respectée)" });
            } else if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }





    return (
        <>
            <h2 className="text-secondary text-lg flex-none content-center mb-4">
                Paramètres de la compétition
            </h2>

            <form className="flex flex-col gap-4" onSubmit={(e) => handleSubmit(e)}>

                {/* Premiere ligne : code*/}
                <div className="md:w-[30%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="code">
                            Code <span className="text-red-500">*</span>
                        </label>
                        <input type="text" className="w-full border border-secondary rounded-md"
                            id="code" name="code" 
                            value={state.apiPayload.code} 
                            onChange={handleChange}
                            required
                            />
                    </div>
                </div>
                {/* Deuxieme ligne : nom */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="name">
                            Nom <span className="text-red-500">*</span>
                        </label>
                        <input type="text" className="w-full border border-secondary rounded-md"
                            id="name" name="name" 
                            value={state.apiPayload.name} 
                            onChange={handleChange}
                            required
                            />
                    </div>
                </div>

                {/* Ligne : Active */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-row gap-2 flex-auto">
                        <input type="checkbox" className="w-6 h-6 border border-secondary rounded-md"
                            id="active" name="active" 
                            checked={state.apiPayload.active} 
                            onChange={handleChange}
                            />
                        <label className="text-primary text-base" htmlFor="active">
                            Activée
                        </label>
                    </div>
                </div>

                {/* Ligne : Default */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-row gap-2 flex-auto">
                        <input type="checkbox" className="w-6 h-6 border border-secondary rounded-md"
                            id="default" name="default" 
                            checked={state.apiPayload.default} 
                            onChange={handleChange}
                            />
                        <label className="text-primary text-base" htmlFor="default">
                            Compétition par défaut (désactivera l&apos;autre compétition actuellement par défaut)
                        </label>
                    </div>
                </div>

                {/* Ligne : gestion des métiers associés */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex-1 flex flex-col gap-2 bg-slate-50 border border-secondary rounded-md px-2 py-1 ">
                        <h3 className="text-secondary">Métiers disponibles</h3>
                        {skills?.filter((skill) => !state.apiPayload.skill_ids.includes(skill.id)).map((skill, i) => {
                            return (
                                <div key={i} 
                                    className="w-full flex flex-row justify-between hover:bg-slate-200 hover:rounded-md px-1 cursor-pointer hover:text-green-600"
                                    onClick={(e) => {e.preventDefault();dispatch({ type: ACTIONS.ADD_SKILL_ID, payload: skill.id })}}
                                >
                                    <span className="text-base">{skill.code} - {skill.name}</span>
                                    <ArrowBigRight size={24} className="" />
                                </div>
                            )
                        })}
                    </div>
                    <div className="flex flex-col gap-2 flex-1  bg-slate-50 border border-secondary rounded-md px-2 py-1 ">
                    <h3 className="text-secondary">Métiers associés</h3>
                        {state?.apiPayload?.skill_ids?.map((id, i) => {
                            const skill = skills.find((skill) => skill.id === id)
                            return (
                                <div key={i} 
                                    className="w-full flex flex-row justify-left hover:bg-slate-200 hover:rounded-md hover:text-red-600 px-1 cursor-pointer"
                                    onClick={(e) => {e.preventDefault();dispatch({ type: ACTIONS.REMOVE_SKILL_ID, payload: skill.id })}}
                                >
                                    <ArrowBigLeft size={24} className="text-primary hover:text-blue-600" />
                                    <span className="text-base">{skill.code} - {skill.name}</span>
                                    
                                </div>
                            )
                        })}
                    </div>
                </div>  
                <div className="w-[50%] gap-5 flex flex-row items-center">
                    
                    <button className="flex gap-2 items-center border border-blue-500 rounded-md px-2 py-1 text-base hover:bg-blue-200"
                        onClick={(e) => handleSubmit(e)}
                        disabled={state.isLoading}
                    >
                        <Save size={24} className="text-primary" />
                        Sauvegarder
                    </button>
                    
                    {
                        state?.error_message && <span className="text-red-700 flex-none content-center mr-4">{state.error_message}</span>
                    }
                </div>
            </form>
        </>
    )
}